import {
  Avatar, Box, Button, Checkbox, Chip, Container, Divider, FormControl, FormControlLabel,
  FormGroup, FormHelperText, FormLabel, InputBase, InputLabel, Link, Paper, Popover, Toolbar, Tooltip, tooltipClasses, Typography,
} from '@mui/material';
import { alpha, styled, useTheme } from '@mui/material/styles';
import { useRouter } from 'next/router';
import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import useSWR from 'swr';
import useMediaQuery from '@mui/material/useMediaQuery';
import { LoadingButton } from "@mui/lab";

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ShareIcon from '@mui/icons-material/Share';

import { roundNum } from '../../../helpers';
import { convertDate, getTradingItem } from '../../../helpers/time';
import { convertTime12to24 } from "../../../utils/time";
import { dataConstants } from '../../../constants';
import { useFetchTabData } from "../../../utils/helpers";
import useAuth from "../../../hook/auth";
import { needPremium } from '../../../needPremium';

import _getTickerInfo from '../../../../pages/api/_getTickerInfo';
import * as send from '../../../../lib/analytics/send';
import addToWatchlist from "../../../../lib/watchlist/_addToWatchlist";
import deleteFromWatchlist from "../../../../lib/watchlist/_deleteFromWatchlist";
import getWatchlist from "../../../../lib/watchlist/_getWatchlist";

import BreadCrumb from "../BreadCrumb";
import ShareThis from "../ShareThis";
import ScoreMetric from '../../Stock/TickerTabs/Summary/ScoreMetric';
import ValuationMetrics from '../../Stock/TickerTabs/Summary/ValuationMetrics';
import PriceTargets from '../../Stock/TickerTabs/Summary/PriceTargets';

const { STOCK_SECTION_CODES } = dataConstants;

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: '#174c86',
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#174c86',
    color: '#fff',
    boxShadow: theme.shadows[1],
    fontSize: 14,
    fontWeight: 500
  },
}));

const Item = styled(Paper)(({ theme }) => ({
  ...theme.typography.body2,
  height: '100%',
  color: theme.palette.text.secondary,
  boxShadow: theme.palette.mode === 'dark' ? 0 : '0 0.5px 1px rgb(132 135 154 / 20%), 0 2px 3px rgb(132 135 154 / 10%)',
  border: '1px solid #eae9ee',
  textAlign: 'center',
  width: '44%',
  marginBottom: "7px",
  '& .Item-Head': {
    padding: '4px 6px',
    textAlign: 'center',
    '& a': {
      textDecoration: 'none',
    },
    '& .option-title': {
      fontWeight: 500,
      color: '#000',
      fontSize: '.875rem',
      paddingBottom: 0,
    }
  },
  '& .Item-Body': {
    fontWeight: 600,
    color: '#000',
    paddingTop: 0,
    textAlign: 'center',
    backgroundColor: '#fff',
  }
}));

const WatchlistInput = styled(InputBase)(({ theme }) => ({
  'label + &': {
    marginTop: theme.spacing(3),
  },
  '& .MuiInputBase-input': {
    borderRadius: 4,
    position: 'relative',
    backgroundColor: theme.palette.mode === 'light' ? '#F3F6F9' : '#1A2027',
    border: '1px solid',
    borderColor: theme.palette.mode === 'light' ? '#E0E3E7' : '#2D3843',
    fontSize: '.875rem',
    width: '25.5ch',
    padding: '10px 12px',
    transition: theme.transitions.create([
      'border-color',
      'background-color',
      'box-shadow',
    ]),
    '&:focus': {
      boxShadow: `${alpha(theme.palette.primary.main, 0.25)} 0 0 0 0.2rem`,
      borderColor: theme.palette.primary.main,
    },
  },
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  alignItems: 'flex-start',
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(2),
  '@media all': {
    minHeight: 100,
  },
}));

const getGaugePercent = (info, arrPrice) => {
  const { realtime_stock_data } = info || {};
  const currPrice = realtime_stock_data && realtime_stock_data.length > 0 ? realtime_stock_data[0].price : undefined;

  const sortedArrPrice = arrPrice && arrPrice.length > 0 ? [...arrPrice].sort((a, b) => a - b) : null;

  const gaugeDivisions = 6;
  const arrReduced = sortedArrPrice && sortedArrPrice.length > 0 ? sortedArrPrice.map(a => currPrice - a) : null;
  const arrIndex = arrReduced && arrReduced.length > 0 ? arrReduced.filter(a => a > 0).length : null;

  const basePct = arrIndex ? (arrIndex - 1) * (1 / gaugeDivisions) : undefined;
  const diffLevel = arrIndex && sortedArrPrice
    ? (arrIndex >= sortedArrPrice.length ? sortedArrPrice[arrIndex - 1] - sortedArrPrice[arrIndex - 3] : sortedArrPrice[arrIndex] - sortedArrPrice[arrIndex - 1])
    : null;

  const diffPrice = currPrice && arrIndex && sortedArrPrice && sortedArrPrice.length > 0 ? currPrice - sortedArrPrice[arrIndex - 1] : null;
  const addPct = diffLevel && diffPrice ? (diffPrice / diffLevel) * (1 / gaugeDivisions) : null;

  let finalPct = (basePct !== null && basePct !== undefined) && (addPct !== null && addPct !== undefined) ? basePct + addPct : null;
  finalPct = finalPct !== null ? Math.min(1, finalPct) : null;

  return finalPct ? finalPct.toFixed(2) : null;
};

const swrConfig = {
  refreshInterval: 180000,
  onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
    if (error.status === 404) return;
    if (key === 'api/_getTickerInfo') return;
    if (retryCount >= 10) return;
    setTimeout(() => revalidate({ retryCount }), 180000);
  },
};

const TickerToolbar = (props) => {
  const router = useRouter();
  const { asPath } = router;
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { ticker_info, swrTicker, isPremium, showPricingBanner, watchlist_metrics, tabData } = props || {};

  const [color, setColor] = useState('transparent');
  const [clipboard, setClipboard] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [create, setCreate] = useState(false);
  const [watchlists, setWatchlists] = useState([]);
  const [newWatchlist, setNewWatchlist] = useState({
    name: '',
    loading: false,
    isValid: false,
  });

  const colorTimerRef = useRef(null);
  const clipboardTimerRef = useRef(null);

  const summaryColors = useMemo(() => {
    const data = tabData?.find(d => d.section_code === "summary_ranks");
    return data?.settings ? JSON.parse(data.settings) : null;
  }, [tabData]);

  const { data, isValidating } = useSWR(
    swrTicker ? ["api/_getTickerInfo", swrTicker] : null,
    _getTickerInfo,
    swrConfig
  );

  const { getTradingHours, getTickerInfo } = data?.getTickerInfo && data?.getTradingHours ? data : ticker_info || {};
  const { companyName, ticker, realtime_stock_data, long_name } = getTickerInfo || {};

  const handleClick = useCallback((event) => setAnchorEl(event.currentTarget), []);
  const handleClose = useCallback(() => setAnchorEl(null), []);
  const handleCreate = useCallback(() => setCreate(true), []);

  const open = Boolean(anchorEl);
  const id = open ? 'watchlist-popover' : undefined;

  useEffect(() => {
    setColor('#0e9e1936');
    colorTimerRef.current = setTimeout(() => setColor('transparent'), 300);
    return () => {
      if (colorTimerRef.current) clearTimeout(colorTimerRef.current);
    };
  }, [getTickerInfo]);

  const handleClipboard = useCallback(async (value) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const el = document.createElement('textarea');
        el.value = value;
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      }
      setClipboard("Copied");

      if (clipboardTimerRef.current) clearTimeout(clipboardTimerRef.current);
      clipboardTimerRef.current = setTimeout(() => setClipboard(null), 3000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (clipboardTimerRef.current) clearTimeout(clipboardTimerRef.current);
    };
  }, []);

  const diff = realtime_stock_data && realtime_stock_data.length > 1 ? (realtime_stock_data[0]?.price - realtime_stock_data[1]?.price).toFixed(2) : null;
  const percent = diff ? ((diff * 100) / realtime_stock_data[1]?.price).toFixed(2) : null;

  const marketOpen = useMemo(() => {
    if (!getTradingHours?.start_time) return null;
    const timeString = new Date().toLocaleString('en-US', {
      timeZone: getTradingHours?.timezone || 'America/New_York'
    }).split(',')[1]?.trim();

    if (!timeString) return null;
    const newYorkTime = convertTime12to24(timeString);
    return newYorkTime > getTradingHours.start_time && newYorkTime < getTradingHours.end_time;
  }, [getTradingHours]);

  const { data: latest_metrics } = useFetchTabData(ticker, isPremium ? "latest_metrics_premium" : "latest_metrics", "NEW_SUM_OD", "LATP");
  const { data: fv_cheap_vs_expensive } = useFetchTabData(ticker, STOCK_SECTION_CODES.FVPROFILECHEAPEXP, "NEW_SUM_OD", "GAUG");

  const { arrPrice, fv_cheap_vs_expensiveValue } = useMemo(() => {
    if (!latest_metrics?.tabData || !fv_cheap_vs_expensive?.settings?.fv_gauge) {
      return { arrPrice: null, fv_cheap_vs_expensiveValue: null };
    }

    const gaugeKeys = fv_cheap_vs_expensive.settings.fv_gauge.map(d => d.replace(/XXXXX/g, ticker));
    const filteredData = latest_metrics.tabData.filter(d => gaugeKeys.includes(d.code));

    const priceArr = filteredData.length > 0 ? [...filteredData.map(a => a.value), 0] : null;
    const value = getGaugePercent(getTickerInfo, priceArr);

    return { arrPrice: priceArr, fv_cheap_vs_expensiveValue: value };
  }, [fv_cheap_vs_expensive, latest_metrics, ticker, getTickerInfo]);

  const handleNewWatchlist = useCallback((prop) => (event) => {
    setNewWatchlist(prev => ({
      ...prev,
      [prop]: event.target.value,
      loading: false,
      isValid: false,
    }));
  }, []);

  const handleWatchlistCheck = async (event) => {
    const name = event.target.name;
    const isChecked = event.target.checked;
    setNewWatchlist(prev => ({ ...prev, loading: true }));
    try {
      if (isChecked) {
        await addToWatchlist(name, [ticker], null, null, null, user?.accessToken);
        send.uevent(event, `nav-watchlist-${name}-added-${ticker}`);
      } else {
        await deleteFromWatchlist(name, ticker, user?.accessToken);
        send.uevent(event, `nav-watchlist-${name}-removed-${ticker}`);
      }
      if (user?.accessToken) {
        const response = await getWatchlist(user.accessToken);
        setWatchlists(response?.getWatchlist ?? []);
      }
    } catch (err) {
      console.error('Watchlist update failed:', err);
    } finally {
      setNewWatchlist(prev => ({ ...prev, loading: false }));
    }
  };

  const _addToWatchlist = async (event) => {
    event.preventDefault();
    if (!user?.accessToken || !newWatchlist?.name) return;

    setNewWatchlist(prev => ({ ...prev, loading: true }));
    try {
      await addToWatchlist(newWatchlist.name, [ticker], null, null, null, user.accessToken);
      send.uevent(event, `nav-watchlist-${newWatchlist.name}-added-${ticker}`);

      const response = await getWatchlist(user.accessToken);
      setWatchlists(response?.getWatchlist ?? []);
      setCreate(false);
      setNewWatchlist({ name: '', loading: false, isValid: false });
    } catch (err) {
      console.error('Create watchlist failed:', err);
      setNewWatchlist(prev => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    let isMounted = true;
    if (user?.accessToken) {
      getWatchlist(user.accessToken).then(response => {
        if (isMounted) setWatchlists(response?.getWatchlist ?? []);
      }).catch(err => console.error("Failed to load watchlists", err));
    }
    return () => { isMounted = false; };
  }, [user?.accessToken]);

  const following = useMemo(() => watchlists.some((item) => {
    try {
      return JSON.parse(item.tickers).includes(ticker);
    } catch {
      return false;
    }
  }), [watchlists, ticker]);

  return (
    <>
      {ticker &&
        <Box sx={{ background: '#fff' }}>
          <Box sx={{
            background: '#f8f9fa',
            boxShadow: 'inset 0px -8px 24px rgba(214, 227, 255, 0.26)'
          }}>
            <Container maxWidth="xl" sx={{ mb: .5, pb: 0, position: "relative" }}>
              <StyledToolbar
                itemScope itemType="https://schema.org/Corporation"
                sx={{ justifyContent: 'space-between', alignItems: 'start', '&.MuiToolbar-root': { minHeight: '60px !important', pb: 0, px: 0 }, position: 'relative', pt: 2.5 }}>
                <Avatar
                  itemProp="logo"
                  alt={ticker} variant="rounded"
                  src={`https://logo.grufity.com/${ticker}.png`}
                  sx={{
                    width: isMobile ? 50 : 61, height: isMobile ? 50 : 61, mr: 1, mt: .7, background: '#f8f9fa', p: .1, border: '1px solid rgb(218,220,224)', borderRadius: "5px",
                    boxShadow: 0, color: '#174c86', '& img': { objectFit: 'contain', borderRadius: "4px" }
                  }}
                >
                  <ApartmentRoundedIcon />
                </Avatar>
                <Box sx={{ '& .stock-share:hover': { borderBottom: '1px dotted' }, '& .stock-share': { borderBottom: '1px solid transparent' }, width: '100%' }}>
                  {long_name &&
                    <Typography
                      itemProp="name"
                      variant="h1"
                      sx={{
                        color: '#333', mb: .5, mt: .5, mx: .3,
                        fontSize: '1.5rem', fontWeight: 600
                      }}
                    >
                      {`${ticker} - ${long_name} Stock Price, Fair Value and News`}
                    </Typography>}
                  {ticker &&
                    <Box
                      sx={{
                        lineHeight: 1, display: 'flex', alignItems: 'start',
                        '& .hyper-link': { display: 'none', fontSize: '1.1rem' }, '&:hover .hyper-link': { display: 'inline-block' },
                        '&:hover .hyper-share': { display: 'none' }, '& .hyper-link:hover ': { color: '#174c86', cursor: 'pointer' },
                      }}>
                      <Typography
                        variant="p"
                        sx={{ lineHeight: 1, color: 'text.very_light_desc', fontSize: '1.5rem', ml: 0 }}
                      >
                        {realtime_stock_data && realtime_stock_data[0]?.price &&
                          <Typography variant="caption" sx={{ lineHeight: 1, display: 'inline-block', fontSize: '2rem', fontWeight: 600, color: isValidating ? 'rgb(154,160,166)' : '#333', ml: 0 }}>
                            ${roundNum(realtime_stock_data[0].price)}
                          </Typography>}
                        {realtime_stock_data && realtime_stock_data[0]?.price &&
                          <Typography variant="caption"
                            sx={{ lineHeight: 1, display: 'inline-block', color: isValidating ? 'rgb(154,160,166)' : diff < 0 ? 'text.negative' : 'text.positive', fontSize: '1.2rem', fontWeight: 500, mx: 1 }}
                          >
                            {diff && diff > 0 ? `+${diff}` : diff}
                            {realtime_stock_data && realtime_stock_data[0]?.price &&
                              <Typography variant="caption"
                                sx={{ lineHeight: 1, display: 'inline-block', color: isValidating ? 'rgb(154,160,166)' : diff < 0 ? 'text.negative' : 'text.positive', fontWeight: 600, fontSize: '1.2rem' }}
                              >
                                &nbsp;{percent && `(${percent > 0 ? `+${percent}` : percent}%)`}
                              </Typography>}
                          </Typography>}
                      </Typography>
                    </Box>}
                  <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'start', mt: .5, flexWrap: "wrap" }}>
                    <Typography
                      variant="caption"
                      color={!marketOpen ? "warning" : "success"}
                      sx={{
                        lineHeight: 1.28,
                        display: 'inline-block', fontSize: '.875rem',
                        color: isValidating ? 'rgb(154,160,166)' : !marketOpen ? '#A36500' : 'text.positive',
                        mr: 1, my: 1
                      }}
                    >
                      {companyName && (!marketOpen ? `Market Closed` : `Delayed`)}
                      {realtime_stock_data?.[0]?.record_date &&
                        <Typography
                          variant="body1"
                          sx={{
                            lineHeight: 1.28, display: 'inline-block', fontSize: '.875rem', color: isValidating ? 'rgb(154,160,166)' : 'text.very_light_desc'
                          }}
                        >
                          &nbsp;{`as of ${convertDate(getTradingItem({ recordDate: realtime_stock_data[0].record_date })?.recordDate, "dd MMM yyyy, hh:mm aaaaa'm'")} ET`}
                        </Typography>}
                    </Typography>
                    <Box component="div" sx={{ display: { xs: 'none', md: 'inline-block' }, ml: 2, mr: 1.5, }}>
                      <Chip size="small" avatar={following ? <CheckRoundedIcon color="primary" /> : <AddRoundedIcon color="primary" />} aria-describedby={id}
                        sx={{
                          '&:hover': { background: '#f0f0f1' }, color: '#000', '& svg': { color: '#174ea6 !important' }, borderRadius: '5px',
                          background: 'transparent', '& .MuiChip-label': { overflow: 'unset', color: '#174ea6', fontWeight: 600, fontSize: '.875rem' }, px: .7, height: '32px',
                        }}
                        label={following ? "Following" : "Add to Watchlist"} onClick={(e) => { handleClick(e); send.uevent(e, `stock-add-to-watchlist-${ticker}`) }}
                        clickable component="button"
                        data-event-name="add-to-watchlist" data-event-category={`stock-${ticker}`} data-event-label=""
                      />
                    </Box>
                    <Box component="div" sx={{ display: { xs: 'none', md: 'inline-block' }, }}>
                      <ShareThis
                        link={`${process.env.NEXT_PUBLIC_URL}${asPath}`}
                        title={`${long_name} on Grufity`}
                        showButton={false}
                        tooltipText="Share this page"
                        showThisButton={<ShareIcon fontSize="inherit" sx={{ transform: 'rotateY(180deg)', fontSize: '1.1rem' }} />}
                        ticker={ticker}
                      />
                    </Box>
                  </Box>
                </Box>
              </StyledToolbar>
              <Box sx={{ position: isMobile ? "" : "absolute", top: 10, right: 10, display: { xs: 'none', md: 'flex' }, alignItems: 'center', justifyContent: 'space-between', }}>
                <ScoreMetric watchlist_metrics={watchlist_metrics} ticker={ticker} />
              </Box>
            </Container>
          </Box>
          <Container maxWidth="xl" sx={{ pt: .4, pb: .7, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: "wrap", width: '100%' }}>
            <BreadCrumb ticker={ticker} />
            <Box component="div" sx={{ display: { xs: 'block', md: 'none' }, ml: 'auto', mr: 1.5 }}>
              <Chip size="small" avatar={following ? <CheckRoundedIcon color="primary" /> : <AddRoundedIcon color="primary" />} aria-describedby={id}
                sx={{
                  '&:hover': { background: '#f0f0f1' }, color: '#000', '& svg': { color: '#174ea6 !important' }, borderRadius: '5px',
                  background: 'transparent', '& .MuiChip-label': { overflow: 'unset', color: '#174ea6', fontWeight: 600, fontSize: '.875rem' }, px: .7, height: '32px',
                }}
                label={following ? "Following" : "Add to Watchlist"} onClick={(e) => { handleClick(e); send.uevent(e, `stock-add-to-watchlist-${ticker}`) }}
                clickable component="button"
                data-event-name="add-to-watchlist" data-event-category={`stock-${ticker}`} data-event-label=""
              />
            </Box>
          </Container>
          <Box component="div" sx={{ display: { xs: 'block', md: 'none' }, mb: 1, width: '100%', px: 2 }}>
            <ScoreMetric watchlist_metrics={watchlist_metrics} ticker={ticker} />
          </Box>
          <Box component="div" sx={{ display: { xs: 'block', md: 'none' }, mb: 1, width: '100%', px: 2 }}>
            <PriceTargets watchlist_metrics={watchlist_metrics} ticker={ticker} isPremium={isPremium} token={user?.accessToken} />
          </Box>
          <Box component="div" sx={{ display: { xs: 'block', md: 'none' }, mb: 1, width: '100%', px: 2 }}>
            <ValuationMetrics ticker={ticker} realtimeData={getTickerInfo} isPremium={isPremium} token={user?.accessToken} />
          </Box>
        </Box>}

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        sx={{ mt: 1, }}
      >
        {user?.accessToken ?
          <FormControl sx={{ px: 2.4, mt: 2.4, '& .MuiFormControlLabel-label ': { fontSize: '.875rem' }, maxHeight: '190px', overflow: 'scroll' }} component="fieldset" variant="standard">
            <FormLabel component="legend" sx={{ fontSize: '.875rem' }}>Add ticker to:</FormLabel>
            <FormGroup>
              {watchlists && watchlists.length > 0 ? watchlists.map(d => (
                <FormControlLabel
                  key={d?.name}
                  disabled={newWatchlist?.loading && d?.tickers?.includes(`\"${ticker}\"`)}
                  control={<Checkbox size="small" checked={d?.tickers?.includes(`\"${ticker}\"`)} name={d?.name} />}
                  label={d?.name} onChange={handleWatchlistCheck} />
              ))
                : <FormControlLabel control={<Checkbox size="small" checked={false} name={"My Watchlist"} />} label={"My Watchlist"} onChange={handleWatchlistCheck} />}
            </FormGroup>
            <FormHelperText>On the <Link href="/watchlists" underline="hover">watchlists page</Link>, you have the<br /> option to delete or manage your lists.</FormHelperText>
          </FormControl>
          : <FormHelperText sx={{ px: 2, my: 1 }}>Please <Link href="/auth" underline="hover">login</Link> to view all your watchlists.&nbsp;</FormHelperText>}
        <Divider />
        {!create &&
          <Button size="small" startIcon={<AddRoundedIcon />} sx={{ textTransform: 'none', p: 1, width: '100%' }} onClick={handleCreate}>
            New Watchlist
          </Button>}
        {create &&
          <Box
            component="form"
            noValidate
            sx={{ m: 2 }}
            onSubmit={_addToWatchlist}
          >
            <FormControl variant="standard">
              <InputLabel shrink htmlFor="watchlist-input">
                Create new
              </InputLabel>
              <WatchlistInput
                size="small" disabled={!user?.accessToken}
                placeholder="Eg., My Watchlist"
                value={newWatchlist.name}
                onChange={handleNewWatchlist('name')}
                inputProps={{ 'aria-label': 'new_watchlist_name' }}
                id="watchlist-input" />
            </FormControl>
            <Box component="div" sx={{ width: '100%', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: "space-between" }}>
              <Button size="small" sx={{ textTransform: 'none', mt: 1, }} onClick={() => setCreate(false)}>Cancel</Button>
              <LoadingButton
                sx={{ textTransform: 'none', mt: 1, ml: 'auto !important' }}
                onClick={_addToWatchlist}
                disabled={newWatchlist.name === '' || !user?.accessToken}
                loading={newWatchlist?.loading}
                loadingIndicator="Adding..."
                size="small" variant="contained"
              >
                Create
              </LoadingButton>
            </Box>
          </Box>}
      </Popover>
    </>
  );
};

export default React.memo(needPremium(TickerToolbar));